import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  Title,
  Container,
  Button,
  Text,
  Alert,
  Grid,
  Box,
  Group,
  Stack,
  ThemeIcon,
  Center,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconShield,
  IconRocket,
  IconUsers,
  IconCode,
  IconShieldLockFilled,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [error, setError] = useState("");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");
    const errorParam = searchParams.get("error");

    if (token) {
      localStorage.setItem("token", token);
      refreshUser()
        .then(() => {
          navigate("/services");
        })
        .catch(() => {
          setError("Failed to verify SSO session");
        });
    }

    if (errorParam) {
      setError("SSO Login Failed: " + errorParam);
    }
  }, [location, refreshUser, navigate]);

  const handleSsoLogin = () => {
    // Redirect to backend SAML login endpoint
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    window.location.href = `${apiUrl}/auth/saml/login`;
  };

  return (
    <Box
      sx={(theme) => {
        const dark = theme.colorScheme === "dark";
        return {
          minHeight: "100vh",
          background: dark
            ? `
              radial-gradient(circle at 18% 40%, rgba(255, 54, 33, 0.14) 0%, transparent 45%),
              radial-gradient(circle at 82% 70%, rgba(255, 54, 33, 0.08) 0%, transparent 45%),
              #1b1b1b
            `
            : `
              radial-gradient(circle at 18% 40%, rgba(255, 54, 33, 0.10) 0%, transparent 45%),
              radial-gradient(circle at 82% 70%, rgba(255, 54, 33, 0.06) 0%, transparent 45%),
              #fafafa
            `,
          position: "relative",
          overflow: "hidden",
        };
      }}
    >
      <Container
        size="xl"
        sx={{ minHeight: "100vh", position: "relative", zIndex: 1 }}
      >
        {/* Below md the columns stack: size them to content and centre the pair on one screen */}
        <Grid gutter={0} sx={{ minHeight: "100vh", alignContent: "center" }}>
          {/* Left Side - Branding */}
          <Grid.Col span={12} md={6}>
            <Center
              sx={(theme) => ({
                height: "100vh",
                padding: "2rem",
                [theme.fn.smallerThan("md")]: { height: "auto", padding: "2rem 0 1.5rem" },
              })}
            >
              <Stack spacing="xl" align="center">
                {/* Logo/Brand Section */}
                <Box>
                  <Title
                    size="h1"
                    sx={(theme) => ({
                      fontFamily: theme.fontFamily,
                      fontWeight: 650,
                      background: `linear-gradient(135deg, ${theme.colors.thanos[5]}, ${theme.colors.thanos[7]})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: "3rem",
                      lineHeight: 1.2,
                      textAlign: "center",
                      [theme.fn.smallerThan("md")]: { fontSize: "2.25rem" },
                      [theme.fn.smallerThan("xs")]: { fontSize: "2rem" },
                    })}
                  >
                    Small Group Console
                  </Title>
                </Box>

                {/* Feature Icons (desktop only) */}
                <Group
                  spacing="xl"
                  position="center"
                  sx={(theme) => ({ [theme.fn.smallerThan("md")]: { display: "none" } })}
                >
                  <Stack align="center" spacing="xs">
                    <ThemeIcon
                      size={50}
                      radius="xl"
                      sx={(theme) => ({
                        background: `linear-gradient(135deg, ${theme.colors.thanos[5]}, ${theme.colors.thanos[7]})`,
                      })}
                    >
                      <IconRocket size={30} />
                    </ThemeIcon>
                    <Text size="sm" color="dimmed" align="center">
                      MVP Building
                    </Text>
                  </Stack>

                  <Stack align="center" spacing="xs">
                    <ThemeIcon
                      size={50}
                      radius="xl"
                      sx={(theme) => ({
                        background: `linear-gradient(135deg, ${theme.colors.thanos[5]}, ${theme.colors.thanos[7]})`,
                      })}
                    >
                      <IconCode size={30} />
                    </ThemeIcon>
                    <Text size="sm" color="dimmed" align="center">
                      AI Automation
                    </Text>
                  </Stack>

                  <Stack align="center" spacing="xs">
                    <ThemeIcon
                      size={50}
                      radius="xl"
                      sx={(theme) => ({
                        background: `linear-gradient(135deg, ${theme.colors.thanos[5]}, ${theme.colors.thanos[7]})`,
                      })}
                    >
                      <IconUsers size={30} />
                    </ThemeIcon>
                    <Text size="sm" color="dimmed" align="center">
                      Team Management
                    </Text>
                  </Stack>
                </Group>

                {/* Tagline */}
                <Box sx={{ textAlign: "center", maxWidth: 400 }}>
                  <Text size="lg" color="dimmed" sx={{ fontStyle: "italic" }}>
                    "Engineers. Entrepreneurs. The Future."
                  </Text>
                  <Text
                    size="sm"
                    color="dimmed"
                    mt="sm"
                    sx={(theme) => ({ [theme.fn.smallerThan("md")]: { display: "none" } })}
                  >
                    Build together with our battle-tested team from Zomato,
                    Expedia, and Wells Fargo
                  </Text>
                </Box>
              </Stack>
            </Center>
          </Grid.Col>

          {/* Right Side - Login Form */}
          <Grid.Col span={12} md={6}>
            <Center
              sx={(theme) => ({
                height: "100vh",
                padding: "2rem",
                // Container already adds a 16px side gutter on phones
                [theme.fn.smallerThan("md")]: { height: "auto", padding: "0 0 2rem" },
              })}
            >
              <Paper
                withBorder
                shadow="xl"
                p={40}
                radius="xl"
                sx={(theme) => {
                  const dark = theme.colorScheme === "dark";
                  return {
                    width: "100%",
                    maxWidth: 400,
                    backgroundColor: dark ? "#242424" : "#ffffff",
                    border: `1px solid ${dark ? "#333333" : "#e0e0e0"}`,
                    [theme.fn.smallerThan("xs")]: { padding: "28px 16px" },
                  };
                }}
              >
                <Stack spacing="xl">
                  <Box>
                    <Title
                      size="h2"
                      sx={(theme) => ({
                        fontFamily: theme.fontFamily,
                        fontWeight: 700,
                        color: theme.colors.thanos[5],
                        marginBottom: "0.5rem",
                        textAlign: "center",
                      })}
                    >
                      Welcome back
                    </Title>
                  </Box>

                  {error && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="red"
                      title="Authentication Error"
                      radius="md"
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="filled"
                    leftIcon={<IconShieldLockFilled size={18} />}
                    onClick={handleSsoLogin}
                  >
                    Login with Small Group
                  </Button>

                  <Group position="center" spacing={6} noWrap>
                    <IconShield size={14} color="#10b981" style={{ flexShrink: 0 }} />
                    <Text size="xs" color="dimmed" align="center">
                      Secure authentication powered by Small Group
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            </Center>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  );
}
